import { useEffect, useState, useCallback } from "react";
import { useAuth } from "../lib/auth";
import { ErrorPanel } from "./ErrorPanel";
import { fmtMoney } from "../desk/format";
import { SectionHead } from "./SectionHead";

// The Marks surface. TWO views on one screen, and the difference between them is load-bearing
// (founder ruling 8 Sep): LIVE MARKS is real — the marks the owner desk produced — and is the
// default. The CORPUS DRY-RUN is an M3/M4 fixture, labelled as not-real IN the view, never only on
// the toggle, so a corpus number can never be misread as a live one.
//
// COLOUR IS SEMANTIC: flags are measurements, rendered as neutral ink. The only warm signal is
// "needs a person" (amber), shown ONLY where a mark genuinely awaits one — a claim conflict, a
// drafted-class hold, or a broken-promotion alarm handoff.

const flagKind = (f) => f.split(" ")[0].replace(/[^a-z_]/gi, "");
const SEVERE = new Set(["one_sided_stack", "clamp_hit", "tenant_credit_unreviewed",
                        "no_comparable", "tenant_registry_unreadable"]);

const FIELD_LABELS = {
  gla_sf: "Building size (GLA)", year_built: "Year built",
  noi: "Net operating income (NOI)", classification: "Building type",
};
const fieldLabel = (f) => FIELD_LABELS[f] || f.replace(/_/g, " ");
const spanWhere = (s) => {
  if (!s) return null;
  if (s.kind === "pdf" && s.page != null)
    return `page ${s.page}`;   // F119: the physical page — the one the click opens and the viewer shows
  if (s.kind === "xlsx" && (s.sheet || s.cell_range))
    return [s.sheet, s.cell_range].filter(Boolean).join(" · ");
  return null;
};

export default function Marks() {
  const [view, setView] = useState("live");
  return (
    <div data-testid="marks-view">
      <SectionHead eyebrow="THE UNDERWRITER · MARKS" testid="marks"
        headline="Every mark, and what it was read from."
        lede="Read-only. No mark is written here." />

      <div className="desk-tabs" role="tablist">
        <button role="tab" data-testid="marks-tab-live" aria-selected={view === "live"}
                className={`desk-tab ${view === "live" ? "on" : ""}`} onClick={() => setView("live")}>
          Live marks
        </button>
        <button role="tab" data-testid="marks-tab-corpus" aria-selected={view === "corpus"}
                className={`desk-tab ${view === "corpus" ? "on" : ""}`} onClick={() => setView("corpus")}>
          Corpus harness<span className="tab-count">regression</span>
        </button>
      </div>

      {view === "live" ? <LiveMarks /> : <CorpusDryRun />}
    </div>
  );
}

// ============================================================================================
// LIVE MARKS — the staff half of the join-report surface. Reads /underwriter/live-marks.
// ============================================================================================
const STATE_LABEL = {
  marked: "marked", undefendable: "no defensible mark", conflict: "claim conflict",
  drafted_class_hold: "drafted-class hold", alarm_handoff: "alarm — handed to a person",
  doc_unreadable: "document we couldn't read — handed to a person",
  no_number: "no number yet", none: "no document filed",
};
// Rate provenance — NEUTRAL ink. A no-comp judgement rate must never read as transaction-backed.
const RATE_BASIS = {
  operator_confirmed: "confirmed rate",
  operator_judgement: "operator judgement · no comp",
  llm_drafted: "drafted rate",
};

function LiveMarks() {
  const { authedFetch } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [open, setOpen] = useState({});

  const load = useCallback(() => {
    setErr("");
    authedFetch("/underwriter/live-marks").then(setData)
      .catch((e) => setErr(e.detail || "Failed to load live marks."));
  }, [authedFetch]);
  useEffect(() => { load(); }, [load]);

  if (err) return <ErrorPanel message={err} onRetry={load} testid="live-marks-error" />;
  if (!data) return <div className="loading" data-testid="live-marks-loading">Loading live marks…</div>;

  const s = data.summary;
  const toggle = (id) => setOpen((o) => ({ ...o, [id]: !o[id] }));

  if (data.cards.length === 0) {
    return (
      <div className="empty-state" data-testid="live-marks-empty">
        <h2>No live buildings yet</h2>
        <p>Nothing has been filed to an owner desk. When an owner hands in paperwork, the mark it
          produces — and the join behind it — reads here.</p>
      </div>
    );
  }

  return (
    <div data-testid="live-marks">
      <div className="stat-row">
        <Stat label="buildings" value={s.buildings} />
        <Stat label="marked" value={s.marked} />
        <Stat label="honest no-mark" value={s.undefendable} />
        <Stat label="awaiting a person" value={s.awaiting_person}
              tone={s.awaiting_person ? "warn" : "ok"} />
      </div>

      <div className="mark-list">
        {data.cards.map((c) => (
          <div className="mark-card" key={c.building_ref} data-testid={`live-${c.building_ref}`}>
            <button className="lm-head" onClick={() => toggle(c.building_ref)}>
              <span className="lm-ref">{c.building_ref}</span>
              {c.label && c.label !== c.building_ref && <span className="lm-label">{c.label}</span>}
              {c.mark_state === "marked" && c.value != null ? (
                <span className="lm-value">
                  {fmtMoney(c.value)}
                  {c.resolved_rate_bps != null &&
                    <span className="lm-rate">{(c.resolved_rate_bps / 100).toFixed(2)}%</span>}
                </span>
              ) : (
                <span className="lm-state-none">{STATE_LABEL[c.mark_state] || c.mark_state}</span>
              )}
              {c.awaits_person && (
                <span className={`lm-await ${c.mark_state === "conflict" ? "lm-await-red" : ""}`}
                      data-testid={`await-${c.building_ref}`}>
                  {c.mark_state === "conflict" ? "disputed" : "needs a person"}
                </span>
              )}
            </button>

            {c.mark_state === "marked" && c.rate_basis && (
              <div className="lm-basis-row" data-testid={`basis-${c.building_ref}`}>
                <span className={`lm-basis lm-basis-${c.rate_basis}`}>{RATE_BASIS[c.rate_basis]}</span>
              </div>
            )}

            {c.awaits_person && c.awaits_reason && (
              <div className={`lm-await-line ${c.mark_state === "conflict" ? "lm-await-line-red" : ""}`}
                   data-testid={`await-reason-${c.building_ref}`}>
                {c.awaits_reason}
              </div>
            )}

            {c.mark_state === "alarm_handoff" && (
              <AlarmClear refBld={c.building_ref} authedFetch={authedFetch} onCleared={load} />
            )}

            {c.flags.length > 0 && (
              <div className="lm-flags" data-testid={`live-flags-${c.building_ref}`}>
                {c.flags.map((f, i) => (
                  <span key={i} className="lm-flag" title={f} data-testid={`flag-${flagKind(f)}`}>
                    {flagKind(f)}</span>
                ))}
              </div>
            )}

            <LiveJoinReport report={c.report} state={c.mark_state}
                            missing={c.missing_item} onArrival={c.on_arrival} refBld={c.building_ref} />

            {open[c.building_ref] && c.rationale.length > 0 && (
              <div className="lm-rationale" data-testid={`live-rationale-${c.building_ref}`}>
                {c.rationale.map((line, i) => (
                  <div key={i} className={`lm-rline ${line.trim().startsWith("⚑") ? "lm-rline-flag" : ""}`}>
                    {line}</div>
                ))}
              </div>
            )}
            {c.rationale.length > 0 && (
              <button className="lm-trail-toggle" data-testid={`trail-${c.building_ref}`}
                      onClick={() => toggle(c.building_ref)}>
                {open[c.building_ref] ? "Hide the rate trail" : "Show the rate trail"}
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// The join report as the owner saw it — candidates in, facts promoted WITH spans, refusals with
// reasons. This is what makes a THIN FILE legible AS a thin file rather than a broken mapper.
function LiveJoinReport({ report, state, missing, onArrival, refBld }) {
  const promoted = report.facts_promoted || [];
  const refusals = report.refusals || [];
  const questions = report.owner_questions || [];
  const offered = report.verbatim_fields_offered || 0;

  if (state === "undefendable") {
    return (
      <div className="lm-jr" data-testid={`jr-${refBld}`}>
        <div className="lm-jr-undef">
          <div className="lm-jr-label">No mark could be defended — this is honest, not an error</div>
          {missing && <p className="lm-jr-missing">{missing}</p>}
          {onArrival && <p className="lm-jr-arrival">{onArrival}</p>}
          {refusals.length > 0 && (
            <ul className="lm-jr-list">
              {refusals.map((r, i) => (
                <li key={i}><span className="lm-jr-field">{fieldLabel(r.field)}</span>
                  <span className="lm-jr-reason">{r.reason}</span></li>
              ))}
            </ul>
          )}
        </div>
      </div>
    );
  }

  if (report.candidates_extracted === 0 && promoted.length === 0) return null;

  return (
    <div className="lm-jr" data-testid={`jr-${refBld}`}>
      <p className="lm-jr-summary">
        Read {report.candidates_extracted} {report.candidates_extracted === 1 ? "property" : "properties"};
        placed <strong>{promoted.length}</strong> {promoted.length === 1 ? "fact" : "facts"} to a
        source{offered > promoted.length ? ` of ${offered} offered` : ""}.
      </p>

      {promoted.length > 0 && (
        <div className="lm-jr-block" data-testid={`jr-promoted-${refBld}`}>
          <div className="lm-jr-label">What was read, and where</div>
          <ul className="lm-jr-list">
            {promoted.map((f, i) => (
              <li key={i}>
                <span className="lm-jr-field">{fieldLabel(f.field)}</span>
                <span className="lm-jr-val">{f.value}</span>
                {spanWhere(f.span) && <span className="lm-jr-span">{spanWhere(f.span)}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}

      {refusals.length > 0 && (
        <div className="lm-jr-block" data-testid={`jr-refusals-${refBld}`}>
          <div className="lm-jr-label">Could not be placed — and why</div>
          <ul className="lm-jr-list">
            {refusals.map((r, i) => (
              <li key={i}><span className="lm-jr-field">{fieldLabel(r.field)}</span>
                <span className="lm-jr-reason">{r.reason}</span></li>
            ))}
          </ul>
        </div>
      )}

      {questions.length > 0 && (
        <div className="lm-jr-block" data-testid={`jr-questions-${refBld}`}>
          <div className="lm-jr-label">Owner was asked</div>
          <ul className="lm-jr-list">
            {questions.map((q, i) => <li key={i} className="lm-jr-q">{q.prompt}</li>)}
          </ul>
        </div>
      )}

      {promoted.length > 0 && promoted.length <= 3 && refusals.length === 0 && (
        <p className="lm-jr-thin" data-testid={`jr-thin-${refBld}`}>
          A short list is not an error — it is what the document held. The mark rests only on facts
          with a page or cell behind them.
        </p>
      )}
    </div>
  );
}

// ============================================================================================
// CORPUS DRY-RUN — the M3/M4 fixture. Not live. Labelled as such IN the view.
// ============================================================================================
const CORPUS_SEVERE = new Set([...SEVERE]);

function CorpusDryRun() {
  const { authedFetch } = useAuth();
  const [data, setData] = useState(null);
  const [readiness, setReadiness] = useState(null);
  const [err, setErr] = useState("");
  const [open, setOpen] = useState({});

  const load = useCallback(() => {
    setErr("");
    authedFetch("/underwriter/dry-run").then(setData)
      .catch((e) => setErr(e.detail || "Failed to load the dry run."));
    authedFetch("/underwriter/step-table-readiness").then(setReadiness).catch(() =>
      setReadiness({
        statement: "Step-table readiness could not be loaded right now. Per the ruling it stays "
          + "deferred and national credit stays binary until a real ingestion pass measures "
          + "per-tenant SF.",
        proposed_trigger: "",
      }));
  }, [authedFetch]);
  useEffect(() => { load(); }, [load]);

  if (err) return <ErrorPanel message={err} onRetry={load} testid="corpus-error" />;
  if (!data) return <div className="loading" data-testid="corpus-loading">Loading the dry run…</div>;

  const d = data.distribution;
  const declines = data.declines || [];
  const swing = data.swing_watch || [];
  const toggle = (id) => setOpen((o) => ({ ...o, [id]: !o[id] }));
  const marks = [...data.marks].sort(
    (a, b) => (b.band_check?.distance_bp ?? -1) - (a.band_check?.distance_bp ?? -1));

  return (
    <div data-testid="corpus-dry-run">
      <div className="corpus-warn" data-testid="corpus-fixture-banner">
        <span className="corpus-warn-tag">REGRESSION HARNESS</span>
        <span className="corpus-warn-body">The corpus regression harness — the M3/M4 fixture marks it
          runs the engine against to catch regressions. This is the harness's output surface, not live
          owner marks: no number here has an owner behind it. Live marks are on the other tab. (When the
          S3 harness gets its own reader, this view retires.)</span>
      </div>

      {readiness && (
        <div className="readiness-line" data-testid="step-table-readiness">
          <span className="readiness-tag">step-table readiness</span>
          <span className="readiness-body">
            {readiness.statement}
            <span className="readiness-trigger"> {readiness.proposed_trigger}</span>
          </span>
        </div>
      )}

      <div className="stat-row">
        <Stat label="median" value={`${d.median_pct}%`} />
        <Stat label="min / max" value={`${d.min_pct}% / ${d.max_pct}%`} />
        <Stat label="outliers" value={d.band_outliers} tone={d.band_outliers ? "warn" : "ok"} />
        <Stat label="in-tolerance" value={d.band_in_tolerance} />
        <Stat label="class-A unchecked" value={d.band_unchecked_class_a} />
        <Stat label="clamp hits" value={d.clamp_hits} tone={d.clamp_hits ? "warn" : "ok"} />
        <Stat label="unconfirmed rate / class" value={`${d.unconfirmed_rate} / ${d.unconfirmed_class}`} />
      </div>

      {swing.length > 0 && (
        <section className="swing-panel" data-testid="swing-watch">
          <h2>Swing-watch — the six B vs C properties</h2>
          <p className="group-hint">A band that fits only by withholding a true adjustment is the
            finding, not a regression. Distances are off the resolved rate; raw is pre-clamp.</p>
          <div className="reg-table-wrap">
            <table className="reg-table" data-testid="swing-table">
              <thead><tr>
                <th>Property</th><th className="num">Rate</th><th className="num">Raw / applied</th>
                <th>Band</th><th className="num">Distance</th><th>Status</th><th>Tenant fired</th>
              </tr></thead>
              <tbody>
                {swing.map((w) => (
                  <tr key={w.property_id} data-testid={`swing-${w.property_id}`}>
                    <td className="brand-cell">{w.property_id}</td>
                    <td className="num">{w.rate_pct}%</td>
                    <td className="num">{w.raw_adjustment_bp >= 0 ? "+" : ""}{w.raw_adjustment_bp}
                      {" / "}{w.applied_adjustment_bp >= 0 ? "+" : ""}{w.applied_adjustment_bp}bp
                      {w.clamp_hit && <span className="clamp-tag" title="clamp hit"> clamp</span>}</td>
                    <td>{w.band || "—"}</td>
                    <td className="num">{w.distance_bp == null ? "—" : `${w.distance_bp}bp`}</td>
                    <td><span className={`swing-status swing-${w.status}`}>{w.status || "—"}</span></td>
                    <td>{w.tenant_fired ? <span className="fires-yes">yes</span> : <span className="fires-no">—</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <div className="mark-list">
        {marks.map((m) => (
          <div className="mark-card" key={m.property_id} data-testid={`mark-${m.property_id}`}>
            <button className="mark-head" onClick={() => toggle(m.property_id)}>
              <span className="pid">{m.property_id}</span>
              <span className={`rate ${m.band_check?.status === "outlier" ? "rate-outlier" : ""}`}>
                {(m.resolved_rate_bps / 100).toFixed(2)}%</span>
              <span className="flags">
                {m.flags.map((f, i) => (
                  <span key={i} className={`flag ${CORPUS_SEVERE.has(flagKind(f)) ? "flag-severe" : ""}`} title={f}>
                    {flagKind(f)}</span>
                ))}
              </span>
            </button>
            {open[m.property_id] && (
              <div className="rationale-struct" data-testid={`rationale-${m.property_id}`}>
                <div className="base-line">
                  base <strong>{(m.base_rate_bps / 100).toFixed(2)}%</strong>
                  <span className="muted"> · row {m.base_row.id} {m.base_row.version} · {m.base_row.origin} · matched {m.base_row.matched_level}</span>
                </div>
                <ul className="dev-list">
                  {m.deviations.map((dv, i) => (
                    <li key={i} className={`dev dev-${dv.status}`}>
                      <span className="dev-bps">{dv.fired ? `${dv.bps >= 0 ? "+" : ""}${dv.bps}bp`
                        : dv.status === "skipped_input_not_present" ? "skip" : "——"}</span>
                      <span className="dev-reason">{dv.reason}</span>
                    </li>
                  ))}
                </ul>
                {m.clamp_hit && (
                  <div className="clamp-line">clamp: raw {m.raw_adjustment_bps >= 0 ? "+" : ""}{m.raw_adjustment_bps}bp
                    → applied {m.applied_adjustment_bps >= 0 ? "+" : ""}{m.applied_adjustment_bps}bp (a clamp hit is a human signal)</div>
                )}
                <div className="resolved-line">
                  resolved <strong>{(m.resolved_rate_bps / 100).toFixed(2)}%</strong>
                  <span className="muted"> (Δ {m.applied_adjustment_bps >= 0 ? "+" : ""}{m.applied_adjustment_bps}bp)</span>
                </div>
                {m.flags.length > 0 && (
                  <ul className="flag-list">
                    {m.flags.map((f, i) => (
                      <li key={i} className={CORPUS_SEVERE.has(flagKind(f)) ? "flag-li-severe" : ""}>⚑ {f}</li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        ))}

        {declines.map((x) => (
          <div className="mark-card declined" key={x.property_id} data-testid={`decline-${x.property_id}`}>
            <div className="mark-head static">
              <span className="pid">{x.property_id}</span>
              <span className="rate none">NO MARK</span>
              <span className="flags">
                {x.flags.map((f, i) => <span key={i} className="flag flag-severe" title={f}>{flagKind(f)}</span>)}
              </span>
            </div>
            <div className="decline-reason">{(x.rationale || []).join(" ")}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Stat({ label, value, tone }) {
  return (
    <div className={`stat ${tone || ""}`}>
      <div className="stat-val">{value}</div>
      <div className="stat-label">{label}</div>
    </div>
  );
}

// The ONE owner-facing state staff may act on: a broken-promotion alarm is our breakage, not an owner
// decision. A reason is required — it goes on the escalation record.
function AlarmClear({ refBld, authedFetch, onCleared }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async () => {
    if (!reason.trim() || busy) return;
    setBusy(true); setErr("");
    try {
      await authedFetch(`/underwriter/live-marks/${refBld}/clear-alarm`,
        { method: "POST", body: { reasoning: reason.trim() } });
      onCleared();
    } catch (e) {
      setErr(e.detail || "Could not clear the alarm."); setBusy(false);
    }
  };
  return (
    <div className="lm-alarm-clear" data-testid={`alarm-clear-${refBld}`}>
      <input className="lm-alarm-reason" placeholder="What did you do about it? (goes on the record)"
             value={reason} onChange={(e) => setReason(e.target.value)}
             data-testid={`alarm-reason-${refBld}`} />
      <button className="lm-alarm-btn" onClick={submit} disabled={busy || !reason.trim()}
              data-testid={`alarm-clear-btn-${refBld}`}>
        {busy ? "Clearing…" : "Take & clear this alarm"}
      </button>
      {err && <span className="lm-alarm-err" data-testid={`alarm-err-${refBld}`}>{err}</span>}
    </div>
  );
}
